<form
  [formGroup]="aboutYouComponentForm"
  [class.qui-form-layout__two-to-one]="showSocialProof()"
  class="qui-form-layout__col-center flex flex-col items-start gap-400"
  (submit)="handleSubmit($event)"
  id="quote-form"
>
  @if (allFieldsLocked()) {
    <app-locked-quote-alert></app-locked-quote-alert>
  }
  <app-form-card class="w-full" [title]="pageTitle()" icon="lucideCircleUserRound" formDisplayAddress="{{ formDisplayAddress }}" data-testid="about-you-form-card">
    <ng-template #subcopyTemplate>
      <p>Most customers get a home insurance quote in 3 minutes. Kin is a licensed agency committed to offering affordable insurance policies in {{ userState() }}.</p>
    </ng-template>
    <div class="form-field-grid mt-600">
      <app-text-input
        [control]="aboutYouComponentForm.controls.firstName"
        label="First name"
        id="first-name"
        data-testid="first-name"
        [required]="true"
        [errorMessages]="errorMessages.firstName"
        [readonly]="isFieldLocked('firstName')"
      ></app-text-input>

      <app-text-input
        [control]="aboutYouComponentForm.controls.lastName"
        label="Last name"
        id="last-name"
        data-testid="last-name"
        [required]="true"
        [errorMessages]="errorMessages.lastName"
        [readonly]="isFieldLocked('lastName')"
      ></app-text-input>

      <app-text-input
        [control]="aboutYouComponentForm.controls.dob"
        label="Date of birth"
        [maskito]="maskitoDateOptions"
        inputmode="numeric"
        placeholder="MM/DD/YYYY"
        id="dob"
        data-testid="birthdate"
        autocomplete="birthday"
        type="text"
        [required]="true"
        [readonly]="isFieldLocked('dob')"
        [errorMessages]="errorMessages.dob"
        helpText="We need your birth date to get your insurance score for the most accurate quote."
      ></app-text-input>

      <app-text-input
        [control]="aboutYouComponentForm.controls.phoneNumber"
        label="Phone"
        id="phone"
        data-testid="phone"
        type="tel"
        placeholder="(   )    -    "
        [maskito]="maskitoPhoneOptions"
        [required]="true"
        [errorMessages]="errorMessages.phoneNumber"
        helpText="We'll use this to contact you about your policy."
        [readonly]="isFieldLocked('phoneNumber')"
      ></app-text-input>

      <app-text-input
        [control]="aboutYouComponentForm.controls.email"
        class="form-field-full"
        label="Email"
        id="email"
        data-testid="email"
        type="email"
        [required]="true"
        [errorMessages]="errorMessages.email"
        helpText="This will be used to create your account."
        [readonly]="isFieldLocked('email')"
      ></app-text-input>
    </div>
  </app-form-card>
  <div class="card md:px-500 md:py-400">
    <label class="checkbox mb-200 gap-100" for="tcpa-consent" id="tcpa-consent-label" data-testid="tcpa-consent-label">
      <input
        class="checkbox__control h-300 w-300"
        type="checkbox"
        value="yes"
        id="tcpa-consent"
        data-testid="tcpa-consent-checkbox"
        [formControl]="aboutYouComponentForm.controls.hasGivenTcpaConsent"
        #tcpaConsentCheckbox
        [attr.aria-invalid]="aboutYouComponentForm.controls.hasGivenTcpaConsent.invalid && aboutYouComponentForm.controls.hasGivenTcpaConsent.touched"
        [attr.aria-describedby]="
          aboutYouComponentForm.controls.hasGivenTcpaConsent.errors && aboutYouComponentForm.controls.hasGivenTcpaConsent.touched ? 'tcpa-consent-error tcpa-consent-text' : 'tcpa-consent-text'
        "
      />

      <span class="checkbox__label font-regular fs-body-xs flex flex-col gap-100 leading-none" id="tcpa-consent-text">
        @if (showTcpaError()) {
          <div data-testid="tcpa-visibility-error-message" class="error-message form-help--invalid flex items-center gap-100 leading-200" id="tcpa-visibility-error" #tcpaVisibilityError>
            <ng-icon name="lucideCircleAlert" class="!overflow-visible" aria-hidden="true"></ng-icon>
            To proceed, you must scroll to the bottom of the consent details to review all required information
          </div>
        }

        @if (aboutYouComponentForm.controls.hasGivenTcpaConsent.errors && aboutYouComponentForm.controls.hasGivenTcpaConsent.touched) {
          <div data-testid="tcpa-consent-error-message" class="error-message form-help--invalid flex items-center gap-100" id="tcpa-consent-error">
            <ng-icon name="lucideCircleAlert" class="!overflow-visible" aria-hidden="true"></ng-icon>
            Please check the box to continue
          </div>
        }

        <p class="fs-body-xs leading-500" id="tcpa-consent-text">
          I agree to the
          <a href="https://www.kin.com/terms" target="_blank" class="link fs-body-xs"> Kin Terms of Service</a>
          and
          <a href="https://www.kin.com/privacy" target="_blank" class="link fs-body-xs">Privacy Policy</a>
          and authorize Kin Insurance, Inc. and its affiliates and subsidiaries including Elite Insurance Group, Inc., Bright Policy, LLC and Kin Financing, LLC ("Kin") to obtain consumer reports
          including credit-based insurance score, for myself and any co-applicant(s), for financing products and/or insurance policy<sup>1</sup> underwriting or rating,
          <strong>which will not affect my credit score</strong>. I authorize Kin to contact me at the email and/or phone number I provided with respect to the inquiry, including using an automatic
          telephone dialing system and/or prerecorded voice even if my number is on a Do Not Call list. I acknowledge that I can revoke consent at any time by contacting Kin at
          <a href="tel:{{ csPhone }}" class="link fs-body-xs">{{ csPhone | phone }}</a>
          or
          <a href="mailto:support@kin.com" class="link fs-body-xs">support&#64;kin.com</a>.
          <strong>(Required)</strong>
        </p>
      </span>
    </label>

    @if (showEigConsent()) {
      <label class="checkbox mb-200 gap-100" for="eig-consent" id="eig-consent-label" data-testid="eig-consent-label">
        <input
          class="checkbox__control h-300 w-300"
          type="checkbox"
          value="yes"
          id="eig-consent"
          data-testid="eig-consent-checkbox"
          [formControl]="aboutYouComponentForm.controls.hasGivenEigConsent"
          [attr.aria-invalid]="aboutYouComponentForm.controls.hasGivenEigConsent.invalid && aboutYouComponentForm.controls.hasGivenEigConsent.touched"
          [attr.aria-describedby]="
            aboutYouComponentForm.controls.hasGivenEigConsent.errors && aboutYouComponentForm.controls.hasGivenEigConsent.touched ? 'eig-consent-error eig-consent-text' : 'eig-consent-text'
          "
        />
        <span class="flex flex-col gap-100">
          @if (aboutYouComponentForm.controls.hasGivenEigConsent.errors && aboutYouComponentForm.controls.hasGivenEigConsent.touched) {
            <div data-testid="eig-consent-error-message" class="error-message form-help--invalid fs-body-xs flex items-center gap-100 leading-none" id="eig-consent-error">
              <ng-icon name="lucideCircleAlert" class="!overflow-visible" aria-hidden="true"></ng-icon>
              Please check the box to continue
            </div>
          }
          <p class="fs-body-xs leading-500" id="eig-consent-text">
            I agree to the Elite Insurance Group, Inc. ("EIG")
            <a href="https://about.eliteinsurancegroup.com/pages/terms-of-use" target="_blank" rel="noopener noreferrer" class="link fs-body-xs">Terms of Use</a> and
            <a href="https://about.eliteinsurancegroup.com/pages/privacy-policy" target="_blank" rel="noopener noreferrer" class="link fs-body-xs">Privacy Policy</a> and authorize EIG to contact me at
            the email and/or phone number I provided for marketing purposes, including using an automatic telephone dialing system and/or pre-recorded voice even if my number is on a Do Not Call list.
            I acknowledge that I can revoke consent at any time by contacting EIG at <a href="tel:8556644900" class="link fs-body-xs">(855) 664-4900</a> or
            <a href="mailto:insurance@eliteinsurancegroup.com" class="link fs-body-xs">insurance&#64;eliteinsurancegroup.com</a>. <strong>(Required)</strong>
          </p>
        </span>
      </label>
    }

    <label class="checkbox gap-100" for="sms-opt-in" data-testid="sms-opt-in-label" id="sms-opt-in-label">
      <input class="checkbox__control h-300 w-300" type="checkbox" value="yes" id="sms-opt-in" data-testid="sms-checkbox" [formControl]="aboutYouComponentForm.controls.hasOptedIntoSms" />
      <p class="checkbox__label font-regular fs-body-xs leading-500" id="sms-opt-in-text">
        I authorize Kin to contact me at the email and/or phone number I provided for marketing purposes, using SMS texts, an automatic telephone dialing system, and/or prerecorded voice even if my
        number is on a Do Not Call list. I acknowledge that I can revoke consent at any time by contacting Kin at
        <a href="tel:{{ csPhone }}" class="link fs-body-xs">{{ csPhone | phone }}</a>
        or
        <a href="mailto:support@kin.com" class="link fs-body-xs">support&#64;kin.com</a>.
        <strong>(Optional)</strong>
      </p>
    </label>
  </div>
</form>

@if (showSocialProof()) {
  <div class="qui-form-layout__two-to-one__col-rail qui-form-layout__col-rail">
    <app-social-proof data-testid="social-proof"></app-social-proof>
  </div>
}
